import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Box, FormLabel, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DestinationCountry } from '../../types/trip.types';

import styles from './CustomCountryMultiselect.module.scss';

const OPTION_ROW_SX = {
  height: 48,
  minHeight: 48,
  maxHeight: 48,
  boxSizing: 'border-box' as const,
  display: 'flex',
  alignItems: 'center',
  py: 0,
};

type CustomCountryMultiselectProps = {
  options: DestinationCountry[];
  value: DestinationCountry[];
  onChange: (countries: DestinationCountry[]) => void;
  inputLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  customClass?: string;
  autocompleteClassName?: string;
};

const CustomCountryMultiselect: FC<CustomCountryMultiselectProps> = ({
  options,
  value,
  onChange,
  inputLabel = 'Destination Countries',
  placeholder = 'Select countries...',
  disabled = false,
  error = false,
  helperText,
  customClass,
  autocompleteClassName,
}) => {
  const tagsScrollRef = useRef<HTMLSpanElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = tagsScrollRef.current;
    const track = el?.firstElementChild as HTMLElement | null;
    if (!el || !track) {
      setHasOverflow(false);
      return;
    }
    setHasOverflow(track.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    if (value.length === 0) {
      setHasOverflow(false);
      return;
    }

    const runCheck = () => requestAnimationFrame(checkOverflow);
    runCheck();

    const el = tagsScrollRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(runCheck);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [value, checkOverflow]);

  const mergedSlotProps = useMemo(() => {
    return {
      paper: {
        elevation: 0,
        sx: {
          boxShadow: '0px 4px 14px 0px #0000001F',
          borderRadius: '10px',
          overflow: 'hidden',
          mt: 0.5,
        },
      },
      listbox: {
        sx: {
          py: 0,
          maxHeight: 240,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 152, 240, 0.35)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 152, 240, 0.5)',
          },
          scrollbarColor: 'rgba(0, 152, 240, 0.35) transparent',
          scrollbarWidth: 'thin',
        },
      },
    };
  }, []);

  const inputClassName = [
    styles.customInput,
    error ? styles.error : '',
    value.length > 0 ? styles.withCountries : '',
    autocompleteClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      id={styles.customSelectContainer}
      className={customClass}
    >
      <FormLabel className={styles.customSelectLabel}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>

      <Autocomplete
        multiple
        disableClearable
        options={options}
        getOptionLabel={(option: DestinationCountry) => option.name}
        value={value}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        className={inputClassName}
        popupIcon={<ExpandMoreIcon />}
        forcePopupIcon
        slotProps={mergedSlotProps}
        onChange={(_, newValue: DestinationCountry[]) => onChange(newValue)}
        renderOption={(props, option, state) => {
          const { key, ...optionLiProps } = props;
          const isLast = state.index === options.length - 1;
          return (
            <Box
              key={key}
              component="li"
              {...optionLiProps}
              sx={{
                ...OPTION_ROW_SX,
                borderBottom: isLast
                  ? 'none'
                  : '1px solid var(--system-stroke-buttons, #EAEEF8)',
              }}
            >
              {option.name}
            </Box>
          );
        }}
        renderTags={(tagValue: readonly DestinationCountry[], getTagProps) => (
          <span
            ref={tagsScrollRef}
            className={`${styles.tagsScroll} ${hasOverflow ? styles.tagsScrollOverflow : ''}`}
          >
            <span className={styles.tagsTrack}>
              {tagValue.map((country: DestinationCountry, index: number) => {
                const tagProps = getTagProps({ index });
                const { key, ...rest } = tagProps;
                return (
                  <Chip
                    key={key}
                    label={country.name}
                    {...rest}
                    deleteIcon={<CloseIcon sx={{ fontSize: 16, color: '#5E6A81' }} />}
                    sx={{
                      height: 34,
                      minHeight: 34,
                      maxHeight: 34,
                      flexShrink: 0,
                      bgcolor: '#EAF5FF',
                      color: '#222B3A',
                      border: 'none',
                      borderRadius: '50px',
                      boxShadow: 'none',
                      '& .MuiChip-label': {
                        px: '10px',
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: '34px',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: '#5E6A81',
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        width: 16,
                        height: 16,
                        '&:hover': {
                          color: '#5E6A81',
                          backgroundColor: 'transparent',
                        },
                      },
                      '& .MuiTouchRipple-root': {
                        display: 'none',
                      },
                    }}
                  />
                );
              })}
            </span>
          </span>
        )}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            label=""
            placeholder={value.length === 0 ? placeholder : ''}
            error={error}
            helperText={helperText}
          />
        )}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default CustomCountryMultiselect;
